import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import type {
  Attempt,
  AttemptAnswer,
  Backup,
  BackupEntry,
  Course,
  Enrollment,
  Question,
} from "./types";

const COURSES = "quiz_courses";
const QUESTIONS = "quiz_questions";
const ATTEMPTS = "quiz_attempts";
const ENROLLMENTS = "quiz_enrollments";
const BACKUPS = "quiz_backups";

// --- Courses ---------------------------------------------------------------

export async function getCourses(): Promise<Course[]> {
  const snap = await getDocs(query(collection(db, COURSES), orderBy("order")));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Course, "id">) }));
}

export async function getCourse(courseId: string): Promise<Course | null> {
  const snap = await getDoc(doc(db, COURSES, courseId));
  return snap.exists() ? { id: snap.id, ...(snap.data() as Omit<Course, "id">) } : null;
}

// --- Enrollment ------------------------------------------------------------

export async function getEnrollment(email: string): Promise<Enrollment | null> {
  const snap = await getDoc(doc(db, ENROLLMENTS, email.toLowerCase()));
  return snap.exists() ? (snap.data() as Enrollment) : null;
}

// --- Questions -------------------------------------------------------------

export async function getActiveCourseQuestions(
  courseId: string,
): Promise<Question[]> {
  const snap = await getDocs(
    query(
      collection(db, QUESTIONS),
      where("courseId", "==", courseId),
      where("active", "==", true),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Question, "id">) }));
}

/** All questions for a course (active and inactive) — for the admin editor. */
export async function getCourseQuestions(courseId: string): Promise<Question[]> {
  const snap = await getDocs(
    query(collection(db, QUESTIONS), where("courseId", "==", courseId)),
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Question, "id">) }))
    .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
}

export async function getQuestionsByIds(ids: string[]): Promise<Question[]> {
  const results = await Promise.all(
    ids.map(async (id) => {
      const snap = await getDoc(doc(db, QUESTIONS, id));
      return snap.exists()
        ? ({ id: snap.id, ...(snap.data() as Omit<Question, "id">) } as Question)
        : null;
    }),
  );
  return results.filter((q): q is Question => q !== null);
}

// --- Attempts --------------------------------------------------------------

export interface NewAttemptInput {
  userId: string;
  userEmail: string;
  course: Course;
  questionIds: string[];
}

export async function createAttempt(input: NewAttemptInput): Promise<string> {
  const ref = await addDoc(collection(db, ATTEMPTS), {
    userId: input.userId,
    userEmail: input.userEmail.toLowerCase(),
    courseId: input.course.id,
    courseTitle: input.course.title,
    questionIds: input.questionIds,
    answers: {},
    score: 0,
    total: input.questionIds.length,
    status: "in_progress",
    createdAt: Date.now(),
  });
  return ref.id;
}

export async function getAttempt(attemptId: string): Promise<Attempt | null> {
  const snap = await getDoc(doc(db, ATTEMPTS, attemptId));
  return snap.exists() ? { id: snap.id, ...(snap.data() as Omit<Attempt, "id">) } : null;
}

export async function completeAttempt(
  attemptId: string,
  answers: Record<string, AttemptAnswer>,
  score: number,
): Promise<void> {
  await updateDoc(doc(db, ATTEMPTS, attemptId), {
    answers,
    score,
    status: "completed",
    completedAt: Date.now(),
  });
}

export async function getUserAttempts(
  userId: string,
  max = 50,
): Promise<Attempt[]> {
  // Single equality filter only — needs no composite index. We filter to
  // completed attempts and sort by date client-side (per-user counts are small).
  const snap = await getDocs(
    query(collection(db, ATTEMPTS), where("userId", "==", userId)),
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Attempt, "id">) }))
    .filter((a) => a.status === "completed")
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, max);
}

// --- Admin helpers (writes guarded by Firestore rules) ---------------------

export async function upsertCourse(course: Course): Promise<void> {
  const { id, ...data } = course;
  await setDoc(doc(db, COURSES, id), data, { merge: true });
}

export async function upsertQuestion(question: Question): Promise<void> {
  const { id, ...data } = question;
  await setDoc(
    doc(db, QUESTIONS, id),
    { ...data, updatedAt: Date.now() },
    { merge: true },
  );
}

export async function setEnrollment(
  email: string,
  courses: Record<string, boolean>,
): Promise<void> {
  await setDoc(
    doc(db, ENROLLMENTS, email.toLowerCase()),
    { email: email.toLowerCase(), courses, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

/**
 * Full save of a user's enrollment (used by the admin Accesos tab). Overwrites
 * the courses + enrolledAt maps so deselecting a course removes access.
 */
export async function saveEnrollment(
  email: string,
  courses: Record<string, boolean>,
  enrolledAt: Record<string, number>,
): Promise<void> {
  await setDoc(doc(db, ENROLLMENTS, email.toLowerCase()), {
    email: email.toLowerCase(),
    courses,
    enrolledAt,
    updatedAt: Date.now(),
  });
}

/** Edit a single course's registration date for a user (admin). */
export async function setCourseRegistrationDate(
  email: string,
  courseId: string,
  registeredAtMs: number,
): Promise<void> {
  await updateDoc(doc(db, ENROLLMENTS, email.toLowerCase()), {
    [`enrolledAt.${courseId}`]: registeredAtMs,
    updatedAt: Date.now(),
  });
}

export async function listEnrollments(): Promise<Enrollment[]> {
  const snap = await getDocs(collection(db, ENROLLMENTS));
  return snap.docs.map((d) => d.data() as Enrollment);
}

export async function listAdmins(): Promise<string[]> {
  const snap = await getDocs(collection(db, "quiz_admins"));
  return snap.docs.map((d) => d.id);
}

export async function addAdmin(email: string): Promise<void> {
  await setDoc(doc(db, "quiz_admins", email.toLowerCase()), {
    email: email.toLowerCase(),
    addedAt: Date.now(),
  });
}

export async function getCourseQuestionCount(courseId: string): Promise<number> {
  const snap = await getDocs(
    query(collection(db, QUESTIONS), where("courseId", "==", courseId)),
  );
  return snap.size;
}

export async function importQuestions(questions: Question[]): Promise<void> {
  // Sequential to keep it simple and within free-tier write limits.
  for (const q of questions) {
    await upsertQuestion(q);
  }
}

// --- Backups & destructive actions -----------------------------------------
// Every delete / overwrite snapshots the affected docs into `quiz_backups`
// first, so the "Restaurar" tab can put them back.

function currentAdminEmail(): string {
  return auth.currentUser?.email?.toLowerCase() ?? "unknown";
}

/** Split a doc into the (id, data) pair stored inside a backup entry. */
function toEntry<T extends { id: string }>(
  collectionName: string,
  doc: T,
): BackupEntry {
  const { id, ...data } = doc;
  return { collection: collectionName, id, data };
}

async function createBackup(
  label: string,
  kind: Backup["kind"],
  action: Backup["action"],
  entries: BackupEntry[],
): Promise<void> {
  await addDoc(collection(db, BACKUPS), {
    label,
    kind,
    action,
    entries,
    createdAt: Date.now(),
    createdBy: currentAdminEmail(),
  });
}

/** Back up a course + all its questions, then delete every one of them. */
export async function deleteCourse(course: Course): Promise<void> {
  const questions = await getCourseQuestions(course.id);
  const entries: BackupEntry[] = [
    toEntry(COURSES, course),
    ...questions.map((q) => toEntry(QUESTIONS, q)),
  ];
  await createBackup(
    `Curso eliminado: ${course.title} · ${questions.length} pregunta(s)`,
    "course",
    "delete",
    entries,
  );
  for (const q of questions) await deleteDoc(doc(db, QUESTIONS, q.id));
  await deleteDoc(doc(db, COURSES, course.id));
}

/** Back up a single question, then delete it. */
export async function deleteQuestion(question: Question): Promise<void> {
  await createBackup(
    `Pregunta eliminada: ${(question.text || question.id).slice(0, 70)}`,
    "question",
    "delete",
    [toEntry(QUESTIONS, question)],
  );
  await deleteDoc(doc(db, QUESTIONS, question.id));
}

/**
 * Create or overwrite a question. When `previous` is given (an edit) the old
 * version is backed up first; the write is a full replace, not a merge, so
 * fields removed in the editor (e.g. stale choices) really go away.
 */
export async function saveQuestion(
  next: Question,
  previous?: Question,
): Promise<void> {
  if (previous) {
    await createBackup(
      `Pregunta editada: ${(previous.text || previous.id).slice(0, 70)}`,
      "question",
      "edit",
      [toEntry(QUESTIONS, previous)],
    );
  }
  const { id, ...data } = next;
  await setDoc(doc(db, QUESTIONS, id), {
    ...data,
    createdAt: previous?.createdAt ?? data.createdAt ?? Date.now(),
    updatedAt: Date.now(),
  });
}

export async function listBackups(): Promise<Backup[]> {
  const snap = await getDocs(
    query(collection(db, BACKUPS), orderBy("createdAt", "desc")),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Backup, "id">) }));
}

/** Re-create every snapshotted document, then mark the backup as restored. */
export async function restoreBackup(backup: Backup): Promise<void> {
  for (const e of backup.entries) {
    await setDoc(doc(db, e.collection, e.id), e.data);
  }
  await updateDoc(doc(db, BACKUPS, backup.id), { restoredAt: Date.now() });
}

export async function deleteBackup(backupId: string): Promise<void> {
  await deleteDoc(doc(db, BACKUPS, backupId));
}
